import React, { memo, useState } from 'react';
import propTypes from 'prop-types';
;
import { Button, Columns, Column } from 'src/ui/mui-ui-kit';;
import { Field, useField } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { FieldArray } from 'react-final-form-arrays';
import dayjs from 'dayjs-ext';
import {
  UiInputAdapter,
  UiSelectAdapter,
  UiFieldset,
  UiDatePickerAdapter,
  UiToggleAdapter,
} from 'src/ui';
import { composeValidators, email, required } from 'src/ui/validations';
import { PHONE_MASK, ZIP_MASK } from 'src/ui/const';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const familyTitles = {
  Employee_Spouse: 'Spouse',
  Employee_Children: 'Child',
  Employee_Family: 'Family Member',
};

const tooltips = {
  Employee_Spouse:
    'This option is for the primary insured and their spouse only. If you need to add children or other dependents, please select a different coverage option that better fits your family structure.',
  Employee_Children: `This option provides coverage for the primary insured and their legal dependent children. It does not cover a spouse. Select this option if you're seeking coverage for yourself and your children.`,
  Employee_Family:
    'The most comprehensive option, covering the primary insured, their spouse, and their legal dependents, including children. Choose this for full family coverage.',
};

const relationshipFilterFunctions = {
  Employee_Spouse: array => array.filter(item => item.value === 'Spouse'),
  Employee_Children: array =>
    array.filter(
      item => item.value === 'Child' || item.value === 'Legal_Dependent'
    ),
  Employee_Family: array => array.filter(item => item.value !== 'Subscriber'),
};
function FamilyMemberForm({
  index,
  removeFamilyMember,
  genderOptions,
  relationshipOptions,
  selectedCoverage,
}) {
  return (
    <UiFieldset
      title={`${familyTitles[selectedCoverage]} ${
        selectedCoverage !== 'Employee_Spouse' ? index + 1 : ''
      }`}
      tooltip={tooltips[selectedCoverage]}
    >
      <input type="hidden" id="id" name="id" />
      <Columns isMultiline>
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiInputAdapter}
            name={`familyMembers[${index}].firstName`}
            id={`familyMembers[${index}].firstName`}
            required
            label="First Name"
            validate={composeValidators('First Name', required)}
          />
        </Column>
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiInputAdapter}
            name={`familyMembers[${index}].lastName`}
            id={`familyMembers[${index}].lastName`}
            required
            label="Last Name"
            validate={composeValidators('Last Name', required)}
          />
        </Column>
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiSelectAdapter}
            name={`familyMembers[${index}].relationship`}
            id={`familyMembers[${index}].relationship`}
            options={relationshipOptions}
            required
            label="Relationship"
            validate={composeValidators('Relationship', required)}
          />
        </Column>
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiSelectAdapter}
            name={`familyMembers[${index}].gender`}
            id={`familyMembers[${index}].gender`}
            options={genderOptions}
            required
            label="Gender When Born"
            validate={composeValidators('Gender When Born', required)}
          />
        </Column>

        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiDatePickerAdapter}
            name={`familyMembers[${index}].dob`}
            id={`familyMembers[${index}].dob`}
            label="Date of Birth"
            required
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            hasIcons="right"
            validate={composeValidators('Date of Birth', required)}
            maxDate={dayjs()
              .startOf('day')
              .toDate()}
            renderIcons={() => [
              <span className="icon is-small is-right" key="right-icon">
                <FontAwesomeIcon icon={['fal', 'calendar-alt']} size="sm" />
              </span>,
            ]}
          />
        </Column>
      </Columns>
      <div className="buttons is-centered">
        <Button
          isOutlined
          className="is-long"
          isColor="danger"
          onClick={() => removeFamilyMember(index)}
          disabled={index === 0}
        >
          Remove
        </Button>
      </div>
    </UiFieldset>
  );
}

function InsuranceInformationForm({
  relationshipOptions,
  coverageOptions,
  genderOptions,
  insuranceGroupNames,
  isAdmin,
  pop,
  push,
  readyToSubmit,
}) {
  const { input: familyMembersInput } = useField('familyMembers');

  const [showAddFamilyButton, setShowAddFamilyButton] = useState(false);
  const [selectedCoverage, setSelectedCoverage] = useState(null);
  // const guidelines = [
  //   'Coverage Options Guidelines:',
  //   '• Employee Only: Coverage for the primary insured individual only. This option is suitable if you are seeking insurance coverage for yourself without including any family members.',
  //   '• Employee + Spouse: Coverage extends to the primary insured and their spouse. This option is intended for the insured and their marital partner only.',
  //   '• Employee + Child(ren): This option provides coverage for the primary insured and their legal dependents, including children. It is designed to cover the insured and their dependent children, but not the spouse.',
  //   '• Employee + Family: The most comprehensive option, covering the primary insured, their spouse, and their legal dependents, including children. This option is suitable for those seeking insurance coverage for the entire family unit.',
  //   'Note: For any option beyond "Employee Only," relevant family members (spouse, children, or both) must be specified as part of the coverage selection process to complete submission. Ensure you select the option that best fits your needs and include all necessary family members in your coverage plan.',
  // ];

  const handleCoverageChange = event => {
    const newValue = event.value;
    setSelectedCoverage(newValue);
    setShowAddFamilyButton(newValue !== 'Employee_Only');
    if (newValue !== 'Employee_Only' && familyMembersInput.value.length === 0) {
      push('familyMembers', undefined);
    }

    if (newValue === 'Employee_Spouse') {
      familyMembersInput.value.forEach((mem, i) => {
        if (i > 0) {
          pop('familyMembers');
        }
      });
    }
    if (newValue === 'Employee_Only') {
      familyMembersInput.value.forEach(() => {
        pop('familyMembers');
      });
    }
  };

  return (
    <UiFieldset
      title="Primary Insured"
      isFirst
      tooltip="For any option beyond Employee Only, relevant family members (spouse, children, or both) must be specified as part of the coverage selection process to complete submission. Ensure you select the option that best fits your needs and include all necessary family members in your coverage plan"
    >
      <Columns isMultiline className="has-guidelines">
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiInputAdapter}
            name="firstName"
            id="firstName"
            required
            label="First Name"
            validate={composeValidators('First Name', required)}
          />
        </Column>
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiInputAdapter}
            name="lastName"
            id="lastName"
            required
            label="Last Name"
            validate={composeValidators('Last Name', required)}
          />
        </Column>
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiSelectAdapter}
            name="coverage"
            id="coverage"
            options={coverageOptions}
            required
            label="Coverage Type"
            validate={composeValidators('Coverage Type', required)}
          />
          <OnChange name="coverage">
            {value => {
              handleCoverageChange(value);
            }}
          </OnChange>
        </Column>
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiInputAdapter}
            name="primaryNumber"
            id="primaryNumber"
            required
            label="Primary Phone"
            mask={PHONE_MASK}
            hasIcons="right"
            validate={composeValidators('Primary Phone', required)}
            renderIcons={() => [
              <span className="icon is-small is-right" key="right-icon">
                <FontAwesomeIcon icon={['fal', 'phone']} size="sm" />
              </span>,
            ]}
          />
        </Column>
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiInputAdapter}
            name="emailAddress"
            id="emailAddress"
            required
            validate={composeValidators('Email Address', required, email)}
            label="Email Address"
            hasIcons="right"
            renderIcons={() => [
              <span className="icon is-small is-right" key="right-icon">
                <FontAwesomeIcon icon={['fal', 'envelope']} size="sm" />
              </span>,
            ]}
          />
        </Column>

        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiDatePickerAdapter}
            name="dob"
            id="dob"
            label="Date of Birth"
            required
            maxDate={dayjs()
              .startOf('day')
              .toDate()}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            hasIcons="right"
            validate={composeValidators('Date of Birth', required)}
            renderIcons={() => [
              <span className="icon is-small is-right" key="right-icon">
                <FontAwesomeIcon icon={['fal', 'calendar-alt']} size="sm" />
              </span>,
            ]}
          />
        </Column>
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiInputAdapter}
            name="primaryZipcode"
            id="primaryZipcode"
            label="Zip Code"
            required
            mask={ZIP_MASK}
            validate={composeValidators('Zip Code', required)}
          />
        </Column>
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiSelectAdapter}
            name="gender"
            id="gender"
            options={genderOptions}
            required
            label="Gender When Born"
            validate={composeValidators('Gender When Born', required)}
          />
        </Column>
        <Column isSize="1/2" className="is-one-third-fullhd">
          <Field
            component={UiSelectAdapter}
            name="insuranceGroupName"
            id="insuranceGroupName"
            isDisabled={!isAdmin}
            options={insuranceGroupNames}
            label="Group"
          />
        </Column>
      </Columns>
      {showAddFamilyButton && (
        <>
          <div className="buttons is-centered">
            <Button
              isOutlined
              className=" is-gradient-bright is-long"
              onClick={() => push('familyMembers', undefined)}
              disabled={selectedCoverage === 'Employee_Spouse'}
            >
              Add Family Member
            </Button>
          </div>
        </>
      )}
      {/* Conditionally render family member forms based on the selected coverage option */}
      {showAddFamilyButton && (
        <>
          <FieldArray name="familyMembers">
            {({ fields }) =>
              fields.map((name, index) => {
                const filteredRelationshipOptions = relationshipFilterFunctions[
                  selectedCoverage
                ](relationshipOptions);
                const familyMember = fields.value[index];

                if (
                  familyMember &&
                  index < filteredRelationshipOptions.length &&
                  !filteredRelationshipOptions.includes(
                    familyMember.relationship
                  )
                ) {
                  fields.update(index, {
                    ...familyMember,
                    relationship: filteredRelationshipOptions[0],
                  });
                }
                return (
                  <FamilyMemberForm
                    key={name}
                    index={index}
                    removeFamilyMember={() => fields.remove(index)}
                    genderOptions={genderOptions}
                    relationshipOptions={filteredRelationshipOptions}
                    selectedCoverage={selectedCoverage}
                  />
                );
              })
            }
          </FieldArray>
        </>
      )}
      <Field
        component={UiToggleAdapter}
        id="insuranceInformationMarkedComplete"
        name="insuranceInformationMarkedComplete"
        type="checkbox"
        disabled={readyToSubmit}
      >
        Information is Complete and Ready to Submit
      </Field>
    </UiFieldset>
  );
}

InsuranceInformationForm.defaultProps = {
  insuranceGroupNames: [],
  readyToSubmit: false,
};

InsuranceInformationForm.propTypes = {
  coverageOptions: propTypes.arrayOf(
    propTypes.shape({
      label: propTypes.string.isRequired,
      value: propTypes.string.isRequired,
    })
  ).isRequired,
  relationshipOptions: propTypes.arrayOf(
    propTypes.shape({
      label: propTypes.string.isRequired,
      value: propTypes.string.isRequired,
    })
  ).isRequired,
  genderOptions: propTypes.arrayOf(
    propTypes.shape({
      label: propTypes.string.isRequired,
      value: propTypes.string.isRequired,
    })
  ).isRequired,
  push: propTypes.func.isRequired,
  readyToSubmit: propTypes.bool,
  pop: propTypes.func.isRequired,
  isAdmin: propTypes.bool.isRequired,
  insuranceGroupNames: propTypes.array,
};

FamilyMemberForm.propTypes = {
  index: propTypes.number.isRequired,
  removeFamilyMember: propTypes.func.isRequired,
  genderOptions: propTypes.array.isRequired,
  relationshipOptions: propTypes.array.isRequired,
  selectedCoverage: propTypes.string.isRequired,
};
const memoizedInsuranceInformationForm = memo(InsuranceInformationForm);

export { memoizedInsuranceInformationForm as InsuranceInformationForm };
